import React from 'react';
import PropTypes from 'prop-types';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';

import { PlanPickerCard } from 'sectors/pilates/members/modal/MemberCreateModal/parts/PlanPickerCard';
import { MembershipUsageCard } from 'sectors/pilates/members/modal/MemberCreateModal/MembershipUsageCard';
import { LocalErrorBox } from 'sectors/pilates/members/modal/MemberCreateModal/parts/LocalErrorBox';

function numOrNull(x) {
  if (x === '' || x == null) return null;
  const n = Number(String(x).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

const empty = () => ({
  planRef: '',
  customPlan: null,
  planSnapshot: null,
  planPriceTry: null,
  agreedPriceTry: '',
  startAt: null,
  durationMonths: 1,
  usageByItem: [],
});

export function PackageCreatePanel({ open, memberId, startAt, api, onCreated, onCancel }) {
  const [form, setForm] = React.useState(empty());
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setForm(empty());
    setSaving(false);
    setErr(null);
  }, [open]);

  const canSave = !!form?.planSnapshot && String(form?.planSnapshot?.name || '').trim().length > 0;

  const onSave = async () => {
    if (!memberId) return setErr('memberId yok');
    if (!canSave) return setErr('Paket seçmelisin (veya özel paket oluşturmalısın).');

    setSaving(true);
    setErr(null);

    try {
      const start = form.startAt || startAt || todayYmd();

      const usageByItem = (form.usageByItem || [])
        .filter((u) => String(u?.activityName || '').trim().length > 0)
        .map((u) => ({
          activityName: u.activityName,
          usedSessions: u.usedSessions ?? 0,
          usedMakeUp: u.usedMakeUp ?? 0,
          usedGift: u.usedGift ?? 0,
        }));

      const dto = {
        memberId,
        startAt: start,
        durationMonths: form.durationMonths ?? 1,
        planRefId: form.planRef || null,
        planSnapshot: form.planSnapshot,
        planPriceTry: numOrNull(form.planPriceTry),
        agreedPriceTry: numOrNull(form.agreedPriceTry),
        usageByItem,
      };

      const res = await api.createMemberPackage(memberId, dto);
      onCreated?.(res, dto);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Paket oluşturulamadı');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MDBox sx={{ display: 'grid', gap: 1.25 }}>
      <PlanPickerCard
        open={open}
        value={form.planRef}
        onChangeValue={(v) => setForm((s) => ({ ...s, planRef: v }))}
        onChangePlanPayload={(payload) => {
          setForm((prev) => ({
            ...prev,
            planSnapshot: payload ?? null,
            planPriceTry: payload?.priceTry ?? null,
            usageByItem: [],
          }));
        }}
        customPlanPayload={form.customPlan}
        onChangeCustomPlanPayload={(payload) => setForm((s) => ({ ...s, customPlan: payload }))}
        agreedPriceTry={form.agreedPriceTry}
        onChangeAgreedPriceTry={(v) => setForm((s) => ({ ...s, agreedPriceTry: v }))}
      />

      {form?.planSnapshot ? (
        <MembershipUsageCard
          planSnapshot={form.planSnapshot}
          usageByItem={form.usageByItem}
          onChangeUsage={(next) => setForm((s) => ({ ...s, usageByItem: next }))}
        />
      ) : null}

      <LocalErrorBox message={err} />

      <MDBox sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
        <MDButton variant="outlined" color="info" onClick={onCancel} disabled={saving}>
          Vazgeç
        </MDButton>

        <MDButton
          variant="gradient"
          color="success"
          onClick={onSave}
          disabled={!canSave || saving}
          sx={{ borderRadius: '14px' }}
        >
          <Icon sx={{ mr: 0.5, fontSize: 18 }}>save</Icon>
          Oluştur
        </MDButton>
      </MDBox>
    </MDBox>
  );
}

PackageCreatePanel.propTypes = {
  open: PropTypes.bool,
  memberId: PropTypes.any,
  startAt: PropTypes.string,
  api: PropTypes.shape({
    createMemberPackage: PropTypes.func.isRequired,
  }).isRequired,
  onCreated: PropTypes.func,
  onCancel: PropTypes.func,
};
